import "./LabWorkStatus.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { DateTime } from "luxon";
import { useCallback, useEffect, useState } from "react";
import ReactTooltip from "react-tooltip";

import { ApiService, LISWork, LabOrder, Stain, LISLabOrder } from "pwapi";

import { addToast, nextId } from "shared/actions/toastActions";
import useSortedCase from "shared/hooks/useSortedCase";
import { selectSortedCaseDetailBlocks } from "shared/selectors/selectSortedCaseDetail";
import {
  getDateTimefromTimestamp,
  timestampToLocaleDateTimeString,
} from "shared/utils/dates";

import AppError from "AppError";
import AppSpinner from "AppSpinner";
import { useAppDispatch, useAppSelector } from "store/hooks";

const LabOrderStain = ({ labOrder }: { labOrder: LISLabOrder }) => {
  const sortedBlocks = useAppSelector(selectSortedCaseDetailBlocks);
  const labStain = useAppSelector(
    ({ labStains }) => labStains[labOrder.lab_stain_id || 0]
  );
  const stain = useAppSelector(({ stains }) => stains[labStain?.stain_id || 0]);
  const block = sortedBlocks.find((b) => b.id === labOrder.block_id);

  return (
    <div>
      <strong>{stain.abbreviation}</strong>
      <span>
        {stain.category !== Stain.category.ROUTINE && ` ${stain.category}`}
      </span>
      {" - "}
      {block?.full_name}
    </div>
  );
};

const LabWorkStatus = () => {
  const caseDetail = useAppSelector(({ caseDetail }) => caseDetail);
  const sortedCase = useSortedCase(caseDetail);
  // TODO: Move this to a reducer instead of component state
  const [workProcesses, _setWorkProcesses] = useState<LISWork[]>([]);
  const setWorkProcesses = (workProcesses: LISWork[]) => {
    workProcesses.sort(
      (a, b) =>
        getDateTimefromTimestamp(a.created_at || "").diff(
          getDateTimefromTimestamp(b.created_at || "")
        ).milliseconds
    );
    workProcesses.forEach((workProcess) => {
      workProcess.tasks.sort(
        (a, b) =>
          getDateTimefromTimestamp(a.created_at || "").diff(
            getDateTimefromTimestamp(b.created_at || "")
          ).milliseconds
      );
    });
    _setWorkProcesses(workProcesses);
  };
  const [loadedAt, setLoadedAt] = useState<false | null | DateTime>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!caseDetail.id) {
      setWorkProcesses([]);
      setLoadedAt(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    ApiService.lisStatusCase({ id: caseDetail.id })
      .then((response) => {
        setWorkProcesses(response);
        setLoadedAt(DateTime.now());
        setLoading(false);
      })
      .catch(() => {
        setLoadedAt(false);
        setLoading(false);
      });
  }, [caseDetail.id]);

  const onClickRefresh = useCallback(() => {
    if (!caseDetail.id) return;

    setLoading(true);

    ApiService.lisStatusCase({ id: caseDetail.id })
      .then((response) => {
        setWorkProcesses(response);
        setLoadedAt(DateTime.now());
        setLoading(false);
      })
      .catch(() => {
        dispatch(addToast({ message: "test" }, nextId()));
        setLoading(false);
      });
  }, [caseDetail.id]);

  if (loadedAt === null) return <AppSpinner>Loading Lab Status...</AppSpinner>;
  if (loadedAt === false)
    return <AppError hideAdvice>Failed to load Lab Status</AppError>;

  return (
    <>
      <h5>
        {workProcesses.length < 1 ? "No Lab Work " : "Lab Status "}
        loaded at {loadedAt.toLocaleString(DateTime.TIME_WITH_SECONDS)}
        <ReactTooltip id="LabWorkStatus__refresh">
          Refresh Lab Status
        </ReactTooltip>
        <button
          className="LabWorkStatus__refresh"
          onClick={onClickRefresh}
          aria-label="Refresh Lab Status"
          data-tip=""
          data-for="LabWorkStatus__refresh"
          disabled={loading}
        >
          &#x21bb;
        </button>
      </h5>
      {workProcesses.map((workProcess) => (
        <div key={workProcess.id} className="LabWorkStatus--WorkProcess">
          <ReactTooltip id={`work-process-${workProcess.id}-status`}>
            <div className="LabWorkStatus--Task__tooltip">
              {workProcess.order && (
                <div className="LabWorkStatus--Task__tooltip__order">
                  <div>
                    <strong>
                      Lab Order <code>{workProcess.order.id}</code>
                    </strong>
                  </div>
                  {workProcess.order.created_at && (
                    <div>
                      Created{" "}
                      {timestampToLocaleDateTimeString(
                        workProcess.order.created_at
                      )}
                    </div>
                  )}
                  <div>
                    {workProcess.order.completed_at
                      ? `Completed ${timestampToLocaleDateTimeString(
                          workProcess.order.completed_at
                        )}`
                      : "Incomplete"}
                  </div>
                </div>
              )}
              <div>
                <strong>
                  Work Process <code>{workProcess.id}</code>
                </strong>
              </div>
              {workProcess.created_at && (
                <div>
                  Created{" "}
                  {timestampToLocaleDateTimeString(workProcess.created_at)}
                </div>
              )}
              <div>
                {workProcess.completed_at
                  ? `Completed ${timestampToLocaleDateTimeString(
                      workProcess.completed_at
                    )}`
                  : "Incomplete"}
              </div>
            </div>
          </ReactTooltip>
          <div
            className="LabWorkStatus__label"
            data-tip=""
            data-for={`work-process-${workProcess.id}-status`}
          >
            <FontAwesomeIcon
              icon={workProcess.completed_at ? "check-circle" : "play-circle"}
              height="1em"
              className={classNames("LabWorkStatus--Task__status", {
                completed: workProcess.completed_at,
                started: !workProcess.completed_at,
              })}
            />
            <span className="LabWorkStatus--type">
              {workProcess.order?.user_id ? (
                <>{workProcess.order.type} Order</>
              ) : (
                "Routine Work"
              )}
            </span>
          </div>
          {workProcess.order &&
            (workProcess.order.type === LabOrder.type.STAIN ? (
              <LabOrderStain labOrder={workProcess.order} />
            ) : workProcess.order.type === LabOrder.type.RESCAN ? (
              <div>
                {
                  sortedCase.slides?.find(
                    (s) => s.id === workProcess.order?.slide_id
                  )?.full_name_stain
                }
              </div>
            ) : null)}
          {(workProcess.order?.user_id || workProcess.order?.note) && (
            <div className="LabWorkStatus--user">
              {workProcess.order?.note && <>{workProcess.order?.note}</>}
              {workProcess.order?.user_id && workProcess.order?.note && " - "}
              {workProcess.order?.user_id && (
                <>User {workProcess.order.user_id}</>
              )}
            </div>
          )}
          {workProcess.tasks.map((task) => (
            <div key={task.id} className="LabWorkStatus--Task">
              <ReactTooltip id={`task-${task.id}-status`}>
                <div className="LabWorkStatus--Task__tooltip">
                  <div>
                    <strong>
                      {task.type.toUpperCase()} Task <code>{task.id}</code>
                    </strong>
                  </div>
                  {task.created_at && (
                    <div>
                      Created {timestampToLocaleDateTimeString(task.created_at)}
                    </div>
                  )}
                  <div>
                    {task.started_at
                      ? `Started ${timestampToLocaleDateTimeString(
                          task.started_at
                        )}`
                      : "Not Started"}
                  </div>
                  {(task.started_at || task.completed_at) && (
                    <div>
                      {task.completed_at
                        ? `Completed 
                      ${timestampToLocaleDateTimeString(task.completed_at)}`
                        : "Incomplete"}
                    </div>
                  )}
                </div>
              </ReactTooltip>
              <div
                className="LabWorkStatus__label"
                data-tip=""
                data-for={`task-${task.id}-status`}
              >
                <FontAwesomeIcon
                  icon={task.completed_at ? "check-circle" : "play-circle"}
                  height="1em"
                  className={classNames("LabWorkStatus--Task__status", {
                    completed: task.completed_at,
                    started: task.started_at,
                  })}
                />
                <span className="LabWorkStatus--type">{task.type}</span>
              </div>
            </div>
          ))}
        </div>
      ))}
    </>
  );
};

export default LabWorkStatus;
